/**
 * EnergyPdfPreviewDialog — Aperçu du PDF Carte d'identité énergétique avant
 * téléchargement ou envoi au client par email.
 *
 * Workflow :
 *   1. Ouverture → génère le PDF (via POST /energy-card/pdf) et l'affiche dans un iframe
 *   2. Bouton "Télécharger" → download local du blob déjà généré
 *   3. Bouton "Envoyer par email" → ouvre un panneau destinataires + message optionnel
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, Mail, X, Loader2, CheckCircle2, Send } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function EnergyPdfPreviewDialog({ open, onOpenChange, prospect, onGenerated }) {
    const [phase, setPhase] = useState('generating'); // 'generating' | 'preview' | 'email'
    const [pdfBlob, setPdfBlob] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [error, setError] = useState(null);

    // Génération lancée à l'ouverture
    useEffect(() => {
        if (!open || !prospect?.id) return;
        let currentUrl = null;
        setPhase('generating');
        setPdfBlob(null);
        setError(null);
        (async () => {
            try {
                const res = await api.post(
                    `/prospects/${prospect.id}/energy-card/pdf`,
                    {},
                    { responseType: 'blob' },
                );
                const blob = new Blob([res.data], { type: 'application/pdf' });
                currentUrl = URL.createObjectURL(blob);
                setPdfBlob(blob);
                setPdfUrl(currentUrl);
                setPhase('preview');
                onGenerated?.();
            } catch (e) {
                setError(e.response?.data?.detail || 'Erreur génération PDF');
                setPhase('preview');
            }
        })();
        return () => {
            if (currentUrl) URL.revokeObjectURL(currentUrl);
        };
    }, [open, prospect?.id]);

    const download = () => {
        if (!pdfBlob) return;
        const a = document.createElement('a');
        a.href = pdfUrl;
        a.download = `Carte_energetique_${prospect.company_name || prospect.last_name || 'dossier'}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success('Téléchargement démarré');
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl h-[90vh] p-0 overflow-hidden flex flex-col" data-testid="energy-pdf-preview-dialog">
                <DialogHeader className="px-5 py-3 border-b bg-gradient-to-br from-purple-50 to-indigo-50 shrink-0">
                    <div className="flex items-center justify-between gap-2">
                        <DialogTitle className="text-base flex items-center gap-2">
                            <FileText className="w-4 h-4 text-purple-600" />
                            Carte d&apos;identité énergétique — Aperçu
                            {phase === 'generating' && (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-200 ml-1">
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Génération…
                                </Badge>
                            )}
                            {phase === 'preview' && pdfBlob && !error && (
                                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 ml-1">
                                    <CheckCircle2 className="w-3 h-3 mr-1" /> Prêt
                                </Badge>
                            )}
                        </DialogTitle>
                        <div className="flex items-center gap-1.5">
                            {phase === 'preview' && pdfBlob && (
                                <>
                                    <Button size="sm" variant="outline" onClick={download} data-testid="pdf-preview-download-btn">
                                        <Download className="w-3.5 h-3.5 mr-1" /> Télécharger
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => setPhase('email')}
                                        className="bg-purple-600 hover:bg-purple-700 text-white"
                                        data-testid="pdf-preview-email-btn"
                                    >
                                        <Mail className="w-3.5 h-3.5 mr-1" /> Envoyer au client
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex-1 min-h-0 overflow-hidden bg-slate-100 relative">
                    {phase === 'generating' && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                            <Loader2 className="w-10 h-10 text-purple-500 animate-spin" />
                            <p className="text-sm text-slate-600">Génération du PDF premium…</p>
                            <p className="text-xs text-slate-400">Un snapshot immuable est créé.</p>
                        </div>
                    )}
                    {phase === 'preview' && error && (
                        <div className="p-6 text-center text-sm text-rose-600">{error}</div>
                    )}
                    {phase === 'preview' && !error && pdfUrl && (
                        <iframe
                            src={pdfUrl}
                            className="w-full h-full border-0"
                            title="Aperçu de la Carte d'identité énergétique"
                            data-testid="pdf-preview-iframe"
                        />
                    )}
                    {phase === 'email' && (
                        <EmailPanel
                            prospect={prospect}
                            onBack={() => setPhase('preview')}
                            onSent={() => { setPhase('preview'); toast.success('Email envoyé'); }}
                        />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function EmailPanel({ prospect, onBack, onSent }) {
    const [to, setTo] = useState(prospect?.email || '');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    const send = async () => {
        const emails = to.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
        if (emails.length === 0 || !emails.every((e) => /@/.test(e))) {
            toast.error('Renseigne au moins un email valide'); return;
        }
        setSending(true);
        try {
            await api.post(`/prospects/${prospect.id}/energy-card/pdf/email`, {
                to: emails,
                subject: subject || undefined,
                message: message || undefined,
            });
            onSent?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur envoi email');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="absolute inset-0 bg-white overflow-y-auto" data-testid="pdf-email-panel">
            <div className="max-w-xl mx-auto p-5 space-y-4">
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={onBack} data-testid="pdf-email-back-btn">
                        <X className="w-3.5 h-3.5 mr-1" /> Retour à l&apos;aperçu
                    </Button>
                </div>
                <h3 className="text-lg font-bold">Envoyer la Carte par email</h3>
                <p className="text-xs text-slate-500 -mt-2">Un nouveau PDF sera généré et joint à l&apos;email (nouveau snapshot immuable).</p>

                <div>
                    <label className="text-xs font-semibold text-slate-600">Destinataires</label>
                    <Input
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        placeholder="client@societe.fr, contact@societe.fr"
                        data-testid="pdf-email-to"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">Sépare plusieurs emails par une virgule ou un espace.</p>
                </div>

                <div>
                    <label className="text-xs font-semibold text-slate-600">Objet (optionnel)</label>
                    <Input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder={`Votre Carte d'identité énergétique — ${prospect?.company_name || ''}`}
                        data-testid="pdf-email-subject"
                    />
                </div>

                <div>
                    <label className="text-xs font-semibold text-slate-600">Message personnalisé (optionnel)</label>
                    <Textarea
                        rows={5}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Bonjour, veuillez trouver ci-joint votre Carte d'identité énergétique…"
                        data-testid="pdf-email-message"
                    />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={onBack}>Annuler</Button>
                    <Button
                        onClick={send}
                        disabled={sending}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid="pdf-email-send-btn"
                    >
                        <Send className={`w-4 h-4 mr-1 ${sending ? 'animate-pulse' : ''}`} />
                        {sending ? 'Envoi…' : 'Envoyer'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
